import { EditedNotificationEventItem } from './common/edited'
import { RevertedNotificationEventItem } from './common/reverted'
import { FieldDesignChangeStatus } from '../statuses/fdc'
import { FDCConcurrenceLevelSideEffect } from '../concurrenceLevelSideEffects'
import { CommentedNotificationEventItem } from './common/commented'
import {
  AllApprovalsNotificationEventItem,
  PartialApprovalNotificationEventItem,
} from './common/approval'
import { NotificationEventItem } from './NotificationEventItem'
import { NotificationEventEnum } from '.'

const fdc = 'Field Design Change' as const

const Submitted = new NotificationEventItem({
  id: '5cb02bc1-5e53-48cb-8d45-e24e88f9c9a1',
  name: 'Submitted',
  description: `A ${fdc} is sent from ${FieldDesignChangeStatus.Draft.name} to ${FieldDesignChangeStatus.PendingLevelConcurrences.name}`,
})
const Edited = EditedNotificationEventItem({
  id: '6c811ac2-81b5-4b3e-af02-5ec634cf7509',
  editable: fdc,
})
const ResolutionEdited = new NotificationEventItem({
  id: '85553aec-22bd-49ad-b729-965badf6daef',
  name: 'Resolution Edited',
  description: `A ${fdc} Resolution is edited`,
})
const AnyLevelConcurred = PartialApprovalNotificationEventItem({
  id: '5f47c043-7be9-4046-838b-6704a42bcba4',
  name: 'Any Level Concurred',
  description: {
    object: fdc,
    group: 'Concurrence Group',
    approves: 'concurs on the Concurrence Level of',
  },
})
const ConcurrenceLevelRejected = new NotificationEventItem({
  id: '051a831f-17c9-4af2-9814-fa89ad3ebf7a',
  name: 'Concurrence Level Rejected',
  description: `Any User rejects the Concurrence Level on behalf of their Concurrence Group, sending the ${fdc} to ${FieldDesignChangeStatus.Draft.name}`,
})
// There isn't a great way to rename this as past tense
const PendingApprovals = AllApprovalsNotificationEventItem({
  id: '1565404a-1b60-4b1d-af6c-d41f0b792ddb',
  name: 'Pending Approvals',
  description: {
    group: 'Concurrence Group',
    object: fdc,
    sendsTo: FieldDesignChangeStatus.PendingApprovals.name,
    approves: 'concurs on the Concurrence Level of',
  },
})
const GroupApproved = PartialApprovalNotificationEventItem({
  id: '882fc1ca-d999-4450-bec9-bb5921f25576',
  name: 'Group Approved',
  description: {
    group: 'Approval Group',
    object: fdc,
  },
})
const Closed = AllApprovalsNotificationEventItem({
  id: '8713c31d-32b4-4fbc-9e42-ad51334afd52',
  name: 'Closed',
  description: {
    group: 'Approval Group',
    object: fdc,
    sendsTo: FieldDesignChangeStatus.Closed.name,
  },
})
// There is a side effect called Early Closure and it's more important to
// have that association than make this Closed Early
const EarlyClosure = new NotificationEventItem({
  id: '1b09a737-601f-4668-9eb7-60fde5954465',
  name: 'Early Closure',
  description: `A Concurrence Level with the ${FDCConcurrenceLevelSideEffect.EarlyClosure.name} side effect is submitted. This may result in a newly created Design Submittal`,
})
const Reverted = RevertedNotificationEventItem({
  id: '926175e1-6238-4c81-bd3f-d602d5489d74',
  revertable: fdc,
  from: [
    FieldDesignChangeStatus.Closed,
    FieldDesignChangeStatus.PendingApprovals,
  ],
  to: FieldDesignChangeStatus.PendingLevelConcurrences,
})
const Commented = CommentedNotificationEventItem({
  id: '64d43bdf-bea3-48a4-8772-a3389aaab4fd',
  commentable: fdc,
})

export type FDCNotificationEventItem =
  | typeof Submitted
  | typeof Edited
  | typeof ResolutionEdited
  | typeof AnyLevelConcurred
  | typeof ConcurrenceLevelRejected
  | typeof PendingApprovals
  | typeof GroupApproved
  | typeof Closed
  | typeof EarlyClosure
  | typeof Reverted
  | typeof Commented

class FDCNotificationEventEnum extends NotificationEventEnum<
  'FDCNotification',
  FDCNotificationEventItem,
  FDCNotificationEventItem['name'],
  FDCNotificationEventItem['id']
> {
  readonly typename = 'FDCNotification'
  get values() {
    return [
      this.Submitted,
      this.Edited,
      this.ResolutionEdited,
      this.AnyLevelConcurred,
      this.ConcurrenceLevelRejected,
      this.PendingApprovals,
      this.GroupApproved,
      this.Closed,
      this.EarlyClosure,
      this.Reverted,
      this.Commented,
    ]
  }
  readonly Submitted = Submitted
  readonly Edited = Edited
  readonly ResolutionEdited = ResolutionEdited
  readonly AnyLevelConcurred = AnyLevelConcurred
  readonly ConcurrenceLevelRejected = ConcurrenceLevelRejected
  readonly PendingApprovals = PendingApprovals
  readonly GroupApproved = GroupApproved
  readonly Closed = Closed
  readonly EarlyClosure = EarlyClosure
  readonly Reverted = Reverted
  readonly Commented = Commented
}

export const FDCNotificationEvent = new FDCNotificationEventEnum()
